<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import router from '@/router'
import { useDjangoAuthStore } from '@/stores/authDjango'
import { useProjectStore } from '@/stores/project'
import { useAuthorizationStore } from '@/stores/authorization'
import { useProjectUserStore } from '@/stores/projectUser'
import type { Project } from '@/stores/type/project.type'
import {
  AppBackground,
  DeleteDialog,
  ProjectCardGrid,
  ProjectDataTable,
  ProjectHeaderImage
} from '@haskoning/vue-component-library'
import { useStorage } from '@vueuse/core'
import AppSideBar from '@/components/app/AppSideBar.vue'
import ProjectCreateUpdateDialog from '@/components/project/ProjectCreateUpdateDialog.vue'

const authorizationStore = useAuthorizationStore()
const projectStore = useProjectStore()
const projectUserStore = useProjectUserStore()
const authStore = useDjangoAuthStore()

const { user } = storeToRefs(authStore)
const {
  items,
  currentItem,
  createUpdateDialog,
  deleteDialog,
  deleteItem,
  fetchLoading,
  writeLoading,
  deleteLoading,
  fieldsFiltered,
  projectFormErrors
} = storeToRefs(projectStore)

const search = useStorage('projectIndexSearch', '')
const projectIndexViewMode = useStorage('projectIndexViewMode', 'grid')
const authorizationReady = ref(false)
const SPENDING_CAP_FIELD_PARAMETERS = ['compute_spending_cap_eur', 'storage_spending_cap_eur']

const canCreateProject = computed(() => {
  return authorizationStore.hasServicePermission({
    authSource: 'User',
    allowed: ['add_project']
  })
})

const sortedItems = computed(() => {
  const projects = [...(items.value || [])]
  const dateKey = projects.some((project) => 'updatedAt' in project) ? 'updatedAt' : 'date_modified'

  return projects.some((project) => project[dateKey as keyof Project])
    ? projects.sort((a, b) => {
        const aDate = new Date(a[dateKey as keyof Project] as string).getTime()
        const bDate = new Date(b[dateKey as keyof Project] as string).getTime()
        return bDate - aDate
      })
    : projects
})

const navigateToProject = (projectID: number) => {
  router.push({ name: 'project-detail', params: { id: projectID } })
}

const handleAddProject = () => {
  projectStore.clearProjectFormErrors()
  projectStore.setCurrentItem({} as Project)
  projectStore.openCreateUpdateDialog()
}
const handleEditProject = (item: Project) => {
  projectStore.clearProjectFormErrors()
  projectStore.setCurrentItem(item)
  projectStore.openCreateUpdateDialog()
}
const handleDeleteProject = (item: Project) => {
  projectStore.setDeleteItem(item)
  projectStore.openDeleteDialog()
}

const codeField = computed(() => {
  return projectStore.fieldsList.find((field: any) => field.parameter === 'work_order')
})

const canSetProjectSpendingCap = computed(() => {
  if (!currentItem.value?.id) return canCreateProject.value
  return Boolean(currentItem.value?.can_increase_spending_cap)
})

const projectDialogFields = computed(() => {
  if (canSetProjectSpendingCap.value) return fieldsFiltered.value
  return fieldsFiltered.value.filter(
    (field) => !SPENDING_CAP_FIELD_PARAMETERS.includes(field.parameter)
  )
})

const projectDialogDisabledFields = computed(() => [])

const saveProject = async (item: Project) => {
  const savedProject = await projectStore.saveProjectForm(item)
  if (!savedProject) return

  projectStore.closeCreateUpdateDialog()
}

watch(createUpdateDialog, async () => {
  if (!createUpdateDialog.value) {
    projectStore.clearProjectFormErrors()
    projectStore.setCurrentItem({} as Project)
  }
  await projectStore.servicePermissionItems()
})

watch(deleteDialog, () => {
  if (!deleteDialog.value) {
    projectStore.setDeleteItem({} as Project)
  }
})

onMounted(async () => {
  if (!items || !items.value.length) {
    await projectStore.fetchOptions()
    await projectStore.fetchItems()
  }
  try {
    await authorizationStore.fetchItems()
    if (user.value) {
      authorizationStore.setCurrentRole([user.value.role])
    }
  } finally {
    authorizationReady.value = true
  }
  await projectStore.servicePermissionItems()
})

onUnmounted(() => {
  projectStore.resetAll()
  projectUserStore.reset()
  authorizationStore.resetState()
})
</script>

<template>
  <div class="top-level-page fill-height">
    <ProjectHeaderImage :lazy-src="'/header_lazy.jpg'" :src="'/header.jpeg'" />
    <AppBackground>
      <AppSideBar />
      <div class="top-level-content">
        <div v-if="fetchLoading" class="d-flex justify-center align-center fill-height">
          <v-progress-circular indeterminate color="primary" size="64" />
        </div>
        <template v-else>
          <v-card class="position-sticky top-0 project-index-title" elevation="0">
            <v-card-title class="px-3 d-flex justify-space-between project-index-title__bar">
              <div class="d-flex ga-2 justify-center align-center component-title">
                <span>My projects</span>
                <v-btn
                  v-if="canCreateProject"
                  prepend-icon="mdi-plus"
                  class="text-none"
                  color="primary"
                  flat
                  @click="handleAddProject"
                >
                  Add
                </v-btn>
              </div>
              <div class="project-index-title__controls">
                <v-text-field
                  v-model="search"
                  class="project-index-title__search"
                  density="compact"
                  label="Search"
                  prepend-inner-icon="mdi-magnify"
                  variant="outlined"
                  hide-details
                  single-line
                  clearable
                />
                <v-btn-toggle
                  v-model="projectIndexViewMode"
                  divided
                  variant="outlined"
                  density="compact"
                  mandatory
                >
                  <v-btn value="grid">
                    <v-icon>mdi-view-grid</v-icon>
                  </v-btn>
                  <v-btn value="list">
                    <v-icon>mdi-view-list</v-icon>
                  </v-btn>
                </v-btn-toggle>
              </div>
            </v-card-title>
            <v-card-subtitle class="px-3 component-subtitle">
              <span>To start using CloudCompute, please select an existing project below</span
              ><template v-if="canCreateProject">
                or create a new project via the
                <v-btn
                  class="mx-1 cursor-default text-none"
                  prepend-icon="mdi-plus"
                  size="small"
                  color="primary"
                  flat
                  :ripple="false"
                  readonly
                >
                  Add
                </v-btn>
                button </template
              ><span>.</span>
            </v-card-subtitle>
            <v-alert
              v-if="authorizationReady && !canCreateProject"
              class="mx-3 mt-3"
              density="compact"
              icon="mdi-information-outline"
              type="info"
              variant="tonal"
            >
              Project creation is limited to application administrators to control cloud compute
              spending. If you need a new project,
              <a
                class="text-primary font-weight-medium"
                href="mailto:dirk.sliepenbeek@haskoning.com?cc=jaap.van.der.minne@haskoning.com&subject=CloudCompute%20project%20request"
                >contact the CloudCompute team</a
              >.
            </v-alert>
          </v-card>
          <ProjectCardGrid
            v-if="projectIndexViewMode === 'grid'"
            class="mt-6"
            :get-change-permission="projectStore.getChangePermission"
            :get-delete-permission="projectStore.getDeletePermission"
            :items="sortedItems"
            :loading="fetchLoading"
            :search="search"
            work-order-key="work_order"
            @navigate-to-project="navigateToProject"
            @delete-item="handleDeleteProject"
            @edit-item="handleEditProject"
          />
          <ProjectDataTable
            v-else
            class="cursor-pointer mt-6 px-3"
            density="comfortable"
            items-per-page="10"
            :filter-keys="['work_order', 'name', 'location', 'client']"
            :get-change-permission="projectStore.getChangePermission"
            :get-delete-permission="projectStore.getDeletePermission"
            :items="sortedItems"
            :search="search"
            :work-order="{ title: 'Work Order', key: 'work_order' }"
            @navigate-to-project="navigateToProject"
            @delete-item="handleDeleteProject"
            @edit-item="handleEditProject"
          />
        </template>
      </div>
      <ProjectCreateUpdateDialog
        v-if="currentItem"
        v-model="createUpdateDialog"
        :item="currentItem"
        :loading="writeLoading"
        :fields="projectDialogFields"
        :work-order-field="codeField"
        :disabled-fields="projectDialogDisabledFields"
        :errors="projectFormErrors"
        @clear-error="projectStore.clearProjectFormErrors"
        @submit="saveProject"
      />
      <DeleteDialog
        v-if="deleteItem"
        v-model="deleteDialog"
        :loading="deleteLoading"
        :name="deleteItem?.name"
        :item="deleteItem"
        @set-delete-item="deleteItem = $event"
        @delete="projectStore.delete(deleteItem)"
      ></DeleteDialog>
    </AppBackground>
  </div>
</template>

<style scoped>
.top-level-page {
  display: flex;
  flex-direction: column;
}

.top-level-content {
  min-height: 100%;
  width: 100%;
  padding: 16px 32px;
}

:deep(.v-table__wrapper table) {
  border: 1px solid lightgrey;
}

.project-index-title {
  z-index: 2;
}

.project-index-title__bar {
  gap: 16px;
}

.project-index-title__controls {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: center;
  width: min(50%, 520px);
}

.project-index-title__search {
  min-width: 180px;
}

@media (max-width: 760px) {
  .project-index-title__bar {
    align-items: flex-start;
    flex-direction: column;
  }

  .project-index-title__controls {
    justify-content: flex-start;
    width: 100%;
  }
}
</style>
